import format from "date-fns/format";
import React, { Fragment, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { RadioGroup, Radio } from "@palmerhq/radio-group";
import PlanProduct from "../../components/PlanProduct";
import PlanPrice from "../PlanPrice";
import {
  Container,
  PlanCard,
  Savings,
  BundleTitle,
  PlanProductsLayout,
  PlanPriceLayout,
  PlanTerms,
  TrialInfo
} from "./styled/fastrack-suite-plan";

function FastrackSuitePlan({
  price,
  promoCode,
  hasHomebeat,
  suiteMonthly,
  suiteYearly,
  selectedPlanKey,
  onPlanChange
}) {
  const MONTHLY = "suite";
  const YEARLY = "annual_suite";
  const [monthlyPriceAfterDiscount, setMonthlyPriceAfterDiscount] = useState(0);
  const [yearlyPriceAfterDiscount, setYearlyPriceAfterDiscount] = useState(0);

  const PRODUCT_SORT_ORDER = {
    cloud_cma: 1,
    cloud_streams: 2,
    cloud_mlx: 3,
    cloud_attract: 4
  };

  function sortByPreferredProductOrder(a, b) {
    return PRODUCT_SORT_ORDER[a.key] - PRODUCT_SORT_ORDER[b.key];
  }

  const suiteProducts = useMemo(() => {
    return suiteMonthly.products.sort(sortByPreferredProductOrder);
  }, []);

  useEffect(() => {
    const monthlyDiscount = suiteMonthly.discounts.find(
      (d) => d.code === promoCode.toLowerCase()
    );
    setMonthlyPriceAfterDiscount(
      monthlyDiscount
        ? suiteMonthly.price - monthlyDiscount.amount
        : suiteMonthly.price
    );

    const yearlyDiscount = suiteYearly.discounts.find(
      (d) => d.code === promoCode.toLowerCase()
    );
    setYearlyPriceAfterDiscount(
      yearlyDiscount
        ? suiteYearly.price - yearlyDiscount.amount
        : suiteYearly.price
    );
  }, [promoCode]);

  const monthlySavingsWithAnnualPlan = +Math.abs(
    (yearlyPriceAfterDiscount / 12 / monthlyPriceAfterDiscount - 1) * 100
  ).toFixed(0);

  return (
    <Container>
      <RadioGroup
        labelledBy="agreement"
        value={selectedPlanKey}
        onChange={onPlanChange}>
        <Radio value={MONTHLY}>Monthly</Radio>
        <Radio value={YEARLY}>
          Annual{" "}
          {monthlySavingsWithAnnualPlan > 0 && (
            <Savings>(Save {monthlySavingsWithAnnualPlan}%)</Savings>
          )}
        </Radio>
      </RadioGroup>
      <PlanCard>
        <BundleTitle>Suite</BundleTitle>
        <PlanProductsLayout hasHomebeat={hasHomebeat}>
          {suiteProducts.map((p) => (
            <PlanProduct
              key={p.key}
              productKey={p.key}
              isUnavailable={!p.isAvailable}
              hasHomebeat={hasHomebeat}
            />
          ))}
        </PlanProductsLayout>
        {selectedPlanKey === MONTHLY && (
          <Fragment>
            <PlanPriceLayout>
              <PlanPrice
                planKey={suiteMonthly.key}
                planPrice={suiteMonthly.price}
                // finalPrice={monthlyDiscountAmount > 0 ? suiteMonthly.price - monthlyDiscountAmount : suiteMonthly.price}
                finalPrice={price}
                billingCycle={suiteMonthly.billingCycle}
              />
              <PlanTerms>Pay monthly. Cancel Anytime.</PlanTerms>
            </PlanPriceLayout>
          </Fragment>
        )}
        {selectedPlanKey === YEARLY && (
          <Fragment>
            <PlanPriceLayout>
              <PlanPrice
                planKey={suiteYearly.key}
                planPrice={(suiteYearly.price / 12).toFixed(2)}
                // finalPrice={yearlyDiscountAmount > 0 ? suiteYearly.price - yearlyDiscountAmount : suiteYearly.price}
                finalPrice={(yearlyPriceAfterDiscount / 12).toFixed(2)}
                billingCycle={1}
              />
              <PlanTerms>
                Billed as one payment of ${yearlyPriceAfterDiscount.toFixed(0)}
              </PlanTerms>
            </PlanPriceLayout>
          </Fragment>
        )}
      </PlanCard>
      <TrialInfo>
        <h5>A few details about your free trial</h5>
        <p>
          Here’s the deal, you’ll have full access to the tools in your plan
          through the entire trial period.{" "}
          <strong>
            Your trial will end at midnight on{" "}
            {format(window.trial_end_date, "MMMM DD, YYYY")}.
          </strong>{" "}
          After that your plan will be{" "}
          <strong>
            ${price} per {selectedPlanKey === YEARLY ? "year" : "month"}
          </strong>
          . We will send you a reminder email 4 days before the end of your
          trial.
        </p>
      </TrialInfo>
    </Container>
  );
}

FastrackSuitePlan.propTypes = {
  price: PropTypes.number.isRequired,
  promoCode: PropTypes.string,
  hasHomebeat: PropTypes.bool.isRequired,
  selectedPlanKey: PropTypes.oneOf(["suite", "annual_suite"]).isRequired,
  suiteMonthly: PropTypes.object.isRequired,
  suiteYearly: PropTypes.object.isRequired,
  onPlanChange: PropTypes.func.isRequired
};

export default FastrackSuitePlan;
